// OAS Reconciliation Dashboard — Mass Balance enhancement (see MASS_BALANCE_ENHANCEMENT_PLAN.md).
// Ingests three source layouts (auto-classified): OAS Material Download (daily tank inventory),
// OAS Ext Movement (external receipts/deliveries), and Daily Yields (the customer's own per-grade
// balance + MTD loss summary). Two views: a Balance view (a real closing balance when Daily Yields
// is present, else an honest inventory+external-movement reconciliation) and a Density & Temp view
// (Phase 1 diagnostics). The volume<->mass flip is the hero interaction.
import React, { useState, useRef, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box, Paper, Stack, Typography, Chip, Button, ToggleButton, ToggleButtonGroup, TextField,
  MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, IconButton, Tooltip, Alert,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import {
  CheckCircle, RadioButtonUnchecked, Refresh, KeyboardArrowRight, KeyboardArrowDown, InfoOutlined,
  Delete as DeleteIcon, FolderOpen, QuestionAnswer,
} from '@mui/icons-material';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ReferenceLine,
} from 'recharts';
import * as XLSX from 'xlsx';
import { aoraToYieldData } from '../utils/massBalanceRefinery';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ProductMapping, defaultMapping, loadMapping, heuristicSubCat, SUBCATS, CATEGORIES,
} from '../utils/massBalanceMapping';
import {
  InvRow, MovRow, YieldData, YieldRow, classify, yieldsPeriodKey, periodFromFilename,
} from '../utils/massBalanceOas';
import {
  persistUploads, listPeriods, listUploads, deleteUpload, fetchStoredFile, modalPeriod, StoredFile, MBAuthError, MBTabHandle,
} from '../utils/massBalanceStore';
import { useCalChat } from '../contexts/CalChatContext';
import MonthEndChecklist from './MonthEndChecklist';
import MassBalanceDensity from './MassBalanceDensity';
import StreamMapping, { StreamRow } from './StreamMapping';
import {
  StreamMapping as SM, defaultStreamMapping, loadStreamMapping, saveStreamMapping,
  gradeForStream, gradeColor, GRADE_ORDER,
} from '../utils/streamMapping';

const fmt = (v: number, dp = 0): string =>
  v === 0 ? '0' : v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
const signed = (v: number): string => `${v >= 0 ? '+' : ''}${fmt(v)}`;
const subLabel = (id: string): string => SUBCATS.find((s) => s.id === id)?.label || 'Other';
const subCategory = (id: string): string => SUBCATS.find((s) => s.id === id)?.category || 'byproducts';

const METRIC_TABS = ['Measured', 'Reconciled', 'System Adjustment', 'User Adjustment'] as const;
type Metric = typeof METRIC_TABS[number];
type Basis = 'volume' | 'mass';
type View = 'balance' | 'density';

// Per-grade opening/closing/receipts/deliveries over the FULL span of a dataset (whole month,
// no date-range filter) — the building block for the month-over-month comparison. Mirrors the
// `oas` memo's model but as a pure function so it can run on a second (compare) dataset too.
type Amt = { vol: number; mass: number; desc: string };
function buildGradeSummary(invRows: InvRow[], movRows: MovRow[], subIdOf: (desc: string) => string) {
  const dates = Array.from(new Set(invRows.map((r) => r.dateKey).filter(Boolean))).sort();
  const openDay = dates[0]; const closeDay = dates[dates.length - 1];
  const invByDay: Record<string, Record<string, Amt>> = {};
  for (const r of invRows) {
    (invByDay[r.dateKey] ||= {});
    const g = (invByDay[r.dateKey][r.cgid] ||= { vol: 0, mass: 0, desc: r.desc });
    g.vol += r.vol; g.mass += r.mass; if (r.desc) g.desc = r.desc;
  }
  const openInv = invByDay[openDay] || {}; const closeInv = invByDay[closeDay] || {};
  const recv: Record<string, Amt> = {}; const deliv: Record<string, Amt> = {};
  for (const m of movRows) {
    const b = m.ind === 'R' ? recv : m.ind === 'D' ? deliv : null; if (!b) continue;
    const g = (b[m.cgid] ||= { vol: 0, mass: 0, desc: m.desc });
    g.vol += m.vol; g.mass += m.mass; if (m.desc) g.desc = m.desc;
  }
  const cgids = new Set<string>([...Object.keys(openInv), ...Object.keys(closeInv), ...Object.keys(recv), ...Object.keys(deliv)]);
  return Array.from(cgids).map((cgid) => {
    const desc = openInv[cgid]?.desc || closeInv[cgid]?.desc || recv[cgid]?.desc || deliv[cgid]?.desc || cgid;
    return { cgid, desc, subId: subIdOf(desc), opening: openInv[cgid] || { vol: 0, mass: 0 }, closing: closeInv[cgid] || { vol: 0, mass: 0 }, receipts: recv[cgid] || { vol: 0, mass: 0 }, deliveries: deliv[cgid] || { vol: 0, mass: 0 } };
  });
}

// Module-level cache so uploaded data survives this component unmounting (e.g. navigating away
// from the Mass Balance section and back). Seeded into state on mount, mirrored on change; Reset
// clears it. Lives for the session only — nothing is persisted server-side.
const dataCache: { inv: InvRow[]; mov: MovRow[]; yields: YieldData | null; fileNames: string[] } =
  { inv: [], mov: [], yields: null, fileNames: [] };

// The uploaded rows above are session-only (cleared by a page refresh). The raw files, however, ARE persisted
// server-side (persistUploads, keyed by period). LAST_PERIOD_KEY remembers the period the user last worked on so a
// refresh can auto-restore it into the view instead of showing blank. autoHydrateAttempted guards that restore to
// ONCE per page load (module-level -> resets on refresh), and only when the session cache is empty.
const LAST_PERIOD_KEY = 'oas.lastPeriod';

// "2026-08" -> "Aug 2026" for the month-selector chips; passes through 'unknown'/non-matching keys unchanged.
const MB_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (p: string): string => { const m = /^(\d{4})-(\d{2})$/.exec(p); return m ? `${MB_MONTHS[+m[2] - 1]} ${m[1]}` : p; };

const MassBalanceReconciliation = forwardRef<MBTabHandle, { dataset: string; reloadKey?: number; onUploaded?: () => void; isActive?: boolean }>(({ dataset, reloadKey, onUploaded, isActive = true }, ref) => {
  const { getIdTokenClaims } = useAuth0();
  const [inv, setInv] = useState<InvRow[]>(dataCache.inv);
  const [mov, setMov] = useState<MovRow[]>(dataCache.mov);
  const [yields, setYields] = useState<YieldData | null>(dataCache.yields);
  const [fileNames, setFileNames] = useState<string[]>(dataCache.fileNames);
  useEffect(() => { dataCache.inv = inv; }, [inv]);
  useEffect(() => { dataCache.mov = mov; }, [mov]);
  useEffect(() => { dataCache.yields = yields; }, [yields]);
  useEffect(() => { dataCache.fileNames = fileNames; }, [fileNames]);
  const [view, setView] = useState<View>('balance');
  const [basis, setBasis] = useState<Basis>('volume');
  const [metric, setMetric] = useState<Metric>('Measured');
  const [group, setGroup] = useState<string>('ALL');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [mapping, setMapping] = useState<ProductMapping | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [streamMap, setStreamMap] = useState<SM>(defaultStreamMapping());
  const [streamOpen, setStreamOpen] = useState(false);
  const [savingStream, setSavingStream] = useState(false);
  // P2b persistence: stored months + saved-uploads manager
  const [storeMsg, setStoreMsg] = useState<string>('');
  const [authExpired, setAuthExpired] = useState(false); // saved-uploads list came back 401 — token expired, NOT deleted data
  const [fromRefinery, setFromRefinery] = useState(false); // P3e — this dataset's balance was adapted from AORA (no OAS density/temp)
  const [dsLoading, setDsLoading] = useState(false); // loading/parsing the selected dataset's data
  const [periods, setPeriods] = useState<string[]>([]);
  const [activePeriod, setActivePeriod] = useState<string>(''); // the saved month currently shown (drives the month-chip highlight)
  const { setViewContext: calSetViewContext } = useCalChat();
  const [checklistOpen, setChecklistOpen] = useState(false);   // see the note in MassBalanceUtility

  // Publish what is on screen so Cal answers about the dataset/period the user is looking at.
  // Cleared on unmount so a stale period never leaks into a later, unrelated question.
  // Only the visible tab publishes — see the matching note in MassBalanceUtility.
  useEffect(() => {
    if (!isActive) return;
    calSetViewContext({ massBalance: { dataset: dataset || undefined, period: activePeriod || undefined,
                                       view: 'Diagnostic' } });
    return () => calSetViewContext({});
  }, [isActive, dataset, activePeriod, calSetViewContext]);


  const [mgrOpen, setMgrOpen] = useState(false);
  const [mgrPeriod, setMgrPeriod] = useState<string>('');
  const [mgrFiles, setMgrFiles] = useState<StoredFile[]>([]);
  const [mgrLoading, setMgrLoading] = useState(false);
  // P2e — month-over-month compare
  const [cmpPeriod, setCmpPeriod] = useState<string>('');
  const [cmpData, setCmpData] = useState<{ inv: InvRow[]; mov: MovRow[] } | null>(null);
  const [cmpLoading, setCmpLoading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null); // retained only so reset() can clear a stale value

  useEffect(() => {
    if (!dataset) return;
    (async () => {
      const t = (await getIdTokenClaims())?.__raw;
      setMapping((await loadMapping(dataset, t)) || defaultMapping());
      setStreamMap((await loadStreamMapping(dataset, t)) || defaultStreamMapping());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset]);

  // ---- P2d: per-stream size (throughput) + accuracy grade (A/B/C/D) from the Stream Mapping ----
  const streamSizes = useMemo(() => {
    const m = new Map<string, { label: string; size: number }>();
    if (yields) {
      for (const r of yields.rows) m.set(r.desc || r.sap, { label: r.desc || r.sap, size: Math.abs(r.receipts) + Math.abs(r.deliveries) + Math.abs(r.intakeProd) });
    } else {
      for (const r of mov) {
        const key = r.desc || r.cgid; const cur = m.get(key);
        m.set(key, { label: key, size: (cur?.size || 0) + Math.abs((basis === 'volume' ? r.vol : r.mass) || 0) });
      }
    }
    return m;
  }, [yields, mov, basis]);
  const streamRows: StreamRow[] = useMemo(() => Array.from(streamSizes.entries())
    .map(([key, v]) => ({ key, label: v.label, size: v.size })).sort((a, b) => b.size - a.size), [streamSizes]);
  const streamGrade = (key: string): string | undefined => gradeForStream(streamMap, key);
  const gradeBadge = (key: string) => {
    const g = streamGrade(key);
    return g ? (<Box component="span" sx={{ ml: 0.75, px: 0.55, py: '1px', borderRadius: '3px', fontSize: '0.62rem', fontWeight: 700, color: '#fff', bgcolor: gradeColor(g) }}>{g}</Box>) : null;
  };
  const bySizeThenGrade = (aKey: string, bKey: string): number => {
    const sa = streamSizes.get(aKey)?.size ?? 0; const sb = streamSizes.get(bKey)?.size ?? 0;
    if (sb !== sa) return sb - sa;
    return (GRADE_ORDER[streamGrade(aKey) ?? ''] ?? 9) - (GRADE_ORDER[streamGrade(bKey) ?? ''] ?? 9);
  };
  const saveStream = async () => {
    setSavingStream(true);
    try { const t = (await getIdTokenClaims())?.__raw; await saveStreamMapping(streamMap, dataset, t); } finally { setSavingStream(false); setStreamOpen(false); }
  };

  const subIdFor = useMemo(() => {
    const cache: Record<string, string> = {};
    return (desc: string): string => {
      if (cache[desc] !== undefined) return cache[desc];
      const id = (mapping?.assignments[desc]) || heuristicSubCat(desc) || 'byproducts-other';
      cache[desc] = id; return id;
    };
  }, [mapping]);

  const onFiles = async (files: File[], persist = true) => {
    setError(null);
    const addInv: InvRow[] = []; const addMov: MovRow[] = []; let addYields: YieldData | null = null;
    const names: string[] = []; const failed: string[] = [];
    const toPersist: { file: File; kind: string }[] = [];
    // Period hints for uploads with no dated rows (a Daily Yields file on its own): workbook dates, then filename.
    const periodHints: string[] = [];
    for (const f of files) {
      try {
        const wb = XLSX.read(await f.arrayBuffer(), { type: 'array', cellDates: true });
        const res = classify(wb);
        if (res.kind === 'material' && res.inv) { addInv.push(...res.inv); names.push(`${f.name} — inventory (${res.inv.length})`); toPersist.push({ file: f, kind: 'material' }); }
        else if (res.kind === 'movement' && res.mov) { addMov.push(...res.mov); names.push(`${f.name} — movements (${res.mov.length})`); toPersist.push({ file: f, kind: 'movement' }); }
        else if (res.kind === 'yields' && res.yields) {
          addYields = res.yields; names.push(`${f.name} — daily yields (${res.yields.rows.length} grades)`); toPersist.push({ file: f, kind: 'yields' });
          const hint = yieldsPeriodKey(wb) || periodFromFilename(f.name); if (hint) periodHints.push(hint);
        }
        else failed.push(`${f.name} — not a recognized Material Download / Ext Movement / Daily Yields file`);
      } catch {
        failed.push(`${f.name} — unreadable (re-save as .xlsx?)`);
      }
    }
    if (addInv.length) setInv((p) => [...p, ...addInv]);
    if (addMov.length) setMov((p) => [...p, ...addMov]);
    if (addYields) setYields(addYields);
    if (names.length) setFileNames((p) => [...p, ...names]);
    if (failed.length) setError(failed.join(' · '));

    // Persist raw uploads (dedup by content hash) so they're there next time. Skipped when we are
    // re-loading files that are already stored.
    if (persist && toPersist.length) {
      const dateKeys = [...addInv.map((r) => r.dateKey), ...addMov.map((r) => r.dateKey)].filter(Boolean);
      // Dated inventory/movement rows decide the period; otherwise the yields workbook / filename hints; never
      // silently file under 'unknown' — that key breaks the period pickers downstream.
      let period = modalPeriod(dateKeys);
      if (period === 'unknown') period = modalPeriod(periodHints);
      if (period === 'unknown') { const fromNames = toPersist.map((t) => periodFromFilename(t.file.name)).filter((x): x is string => !!x); period = modalPeriod(fromNames); }
      if (period === 'unknown') { setError(`Couldn't work out which month ${toPersist.map((t) => t.file.name).join(', ')} belongs to — not saved. Include the month in the filename (e.g. "Daily Yields August 2026.xlsx") or upload it together with that month's Material/Movement files.`); return; }
      const t = (await getIdTokenClaims())?.__raw;
      const results = await persistUploads('oas', dataset, period, toPersist, t);
      setActivePeriod(period);
      try { localStorage.setItem(LAST_PERIOD_KEY, period); } catch { /* storage unavailable — ignore */ }
      const stored = results.filter((r) => r.status === 'stored').length;
      const dups = results.filter((r) => r.status === 'duplicate').length;
      const parts: string[] = [];
      if (stored) parts.push(`${stored} saved to ${period}`);
      if (dups) parts.push(`${dups} duplicate${dups > 1 ? 's' : ''} skipped (already stored)`);
      if (parts.length) setStoreMsg(parts.join(' · '));
      setPeriods(await listPeriods('oas', dataset, t));
      if (stored) onUploaded?.(); // refresh the hub's dataset list/counts (surfaces a brand-new dataset)
    }
  };

  // The shared hub uploader hands us the OAS-family files; run them through our own auto-classifier.
  useImperativeHandle(ref, () => ({ ingest: async (files: File[]) => { await onFiles(files); } }));

  // (the dataset-driven effect below sets periods + restores this dataset's data on dataset change)
  const openManager = async () => {
    setMgrOpen(true); setMgrLoading(true);
    try {
      const t = (await getIdTokenClaims())?.__raw;
      const ps = await listPeriods('oas', dataset, t); setPeriods(ps); setAuthExpired(false);
      const p = mgrPeriod || ps[0] || ''; setMgrPeriod(p);
      setMgrFiles(p ? await listUploads('oas', dataset, p, t) : []);
    } catch (e) { if (e instanceof MBAuthError) setAuthExpired(true); }
    setMgrLoading(false);
  };
  const selectMgrPeriod = async (p: string) => {
    setMgrPeriod(p); setMgrLoading(true);
    const t = (await getIdTokenClaims())?.__raw;
    setMgrFiles(await listUploads('oas', dataset, p, t)); setMgrLoading(false);
  };
  // Restore a saved period's files into the active view. Used by both the manager's "Load" button and the
  // auto-restore-on-refresh effect below. Remembers the period as the last-worked one.
  const hydrateFromPeriod = async (period: string): Promise<number> => {
    if (!period) return 0;
    const t = (await getIdTokenClaims())?.__raw;
    const files = await listUploads('oas', dataset, period, t);
    if (!files.length) return 0;
    const fetched: File[] = [];
    for (const sf of files) { const f = await fetchStoredFile(sf); if (f) fetched.push(f); }
    setInv([]); setMov([]); setYields(null); setFileNames([]);
    await onFiles(fetched, false);
    setActivePeriod(period);
    try { localStorage.setItem(LAST_PERIOD_KEY, period); } catch { /* ignore */ }
    return fetched.length;
  };
  const loadStoredMonth = async () => {
    if (!mgrPeriod) return;
    setMgrLoading(true);
    const n = await hydrateFromPeriod(mgrPeriod);
    setMgrLoading(false); setMgrOpen(false);
    setStoreMsg(`Loaded ${mgrPeriod} (${n} file${n > 1 ? 's' : ''})`);
  };

  // Load the ACTIVE DATASET: whenever `dataset` changes (or on mount), clear the view and restore the
  // most recent saved month for that customer. Switching datasets in the hub re-renders this tab from
  // the newly-selected customer's data. (Tab switches don't remount — the hub toggles visibility.)
  useEffect(() => {
    if (!dataset) return;
    let cancelled = false;
    setDsLoading(true);
    (async () => {
      try {
        const t = (await getIdTokenClaims())?.__raw;
        const ps = await listPeriods('oas', dataset, t);
        if (cancelled) return;
        setAuthExpired(false); setPeriods(ps);
        setInv([]); setMov([]); setYields(null); setFileNames([]); setActivePeriod(''); setFromRefinery(false);
        if (ps.length) {
          await hydrateFromPeriod([...ps].sort().reverse()[0]);
        } else {
          // P3e — refinery-sourced (e.g. Toledo) dataset: no OAS files. Adapt its AORA production into a
          // balance so the OAS Balance view still renders. Density/temp stay empty — AORA has no source.
          const rps = await listPeriods('refinery', dataset, t);
          if (!cancelled && rps.length) {
            const target = [...rps].sort().reverse()[0];
            const files = await listUploads('refinery', dataset, target, t);
            for (const sf of files) {
              if (sf.kind !== 'production') continue;
              const f = await fetchStoredFile(sf); if (!f) continue;
              try {
                const yd = aoraToYieldData(XLSX.read(await f.arrayBuffer(), { type: 'array', cellDates: true }));
                if (yd) { setYields(yd); setFromRefinery(true); break; }
              } catch { /* skip */ }
            }
          }
        }
      } catch (e) { if (e instanceof MBAuthError) setAuthExpired(true); }
      finally { if (!cancelled) setDsLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset, reloadKey]);
  const removeStored = async (sf: StoredFile) => {
    const t = (await getIdTokenClaims())?.__raw;
    if (await deleteUpload('oas', dataset, sf.period, sf.uploadId, t)) {
      setMgrFiles((prev) => prev.filter((x) => x.uploadId !== sf.uploadId));
      setPeriods(await listPeriods('oas', dataset, t));
    }
  };

  // P2e — pull a stored month into a side dataset (parsed, not shown as the active upload) so we
  // can diff it against the currently-loaded month. Selecting the blank option clears the compare.
  const loadCompare = async (period: string) => {
    setCmpPeriod(period);
    if (!period) { setCmpData(null); return; }
    setCmpLoading(true);
    try {
      const t = (await getIdTokenClaims())?.__raw;
      const files = await listUploads('oas', dataset, period, t);
      const cInv: InvRow[] = []; const cMov: MovRow[] = [];
      for (const sf of files) {
        const f = await fetchStoredFile(sf); if (!f) continue;
        try {
          const wb = XLSX.read(await f.arrayBuffer(), { type: 'array', cellDates: true });
          const res = classify(wb);
          if (res.kind === 'material' && res.inv) cInv.push(...res.inv);
          else if (res.kind === 'movement' && res.mov) cMov.push(...res.mov);
        } catch { /* skip unreadable */ }
      }
      setCmpData({ inv: cInv, mov: cMov });
    } finally { setCmpLoading(false); }
  };

  const reset = () => {
    setInv([]); setMov([]); setYields(null); setFileNames([]); setError(null); setExpanded({}); setGroup('ALL');
    if (fileInput.current) fileInput.current.value = '';
  };

  const allDates = useMemo(() => Array.from(new Set(inv.map((r) => r.dateKey).filter(Boolean))).sort(), [inv]);
  useEffect(() => {
    if (allDates.length) { setFromDate((f) => f || allDates[0]); setToDate((t) => t || allDates[allDates.length - 1]); }
  }, [allDates]);
  const effFrom = fromDate || (allDates[0] ?? '');
  const effTo = toDate || (allDates[allDates.length - 1] ?? '');
  const datesInRange = useMemo(() => allDates.filter((d) => d >= effFrom && d <= effTo), [allDates, effFrom, effTo]);

  const val = (r: { vol: number; mass: number }) => (basis === 'volume' ? r.vol : r.mass);
  const unit = basis === 'volume' ? 'm³' : 't';

  // ---- OAS inventory + external-movement model (used when Daily Yields absent) ----
  const oas = useMemo(() => {
    if (!datesInRange.length && !mov.length) return null;
    const openDay = datesInRange[0]; const closeDay = datesInRange[datesInRange.length - 1];
    const invByDay: Record<string, Record<string, { vol: number; mass: number; desc: string }>> = {};
    for (const r of inv) {
      (invByDay[r.dateKey] ||= {});
      const g = (invByDay[r.dateKey][r.cgid] ||= { vol: 0, mass: 0, desc: r.desc });
      g.vol += r.vol; g.mass += r.mass; if (r.desc) g.desc = r.desc;
    }
    const openInv = invByDay[openDay] || {}; const closeInv = invByDay[closeDay] || {};
    const recv: Record<string, { vol: number; mass: number; desc: string }> = {};
    const deliv: Record<string, { vol: number; mass: number; desc: string }> = {};
    for (const m of mov) {
      if (m.dateKey && (m.dateKey < effFrom || m.dateKey > effTo)) continue;
      const b = m.ind === 'R' ? recv : m.ind === 'D' ? deliv : null; if (!b) continue;
      const g = (b[m.cgid] ||= { vol: 0, mass: 0, desc: m.desc });
      g.vol += m.vol; g.mass += m.mass; if (m.desc) g.desc = m.desc;
    }
    const cgids = new Set<string>([...Object.keys(openInv), ...Object.keys(closeInv), ...Object.keys(recv), ...Object.keys(deliv)]);
    const grades = Array.from(cgids).map((cgid) => {
      const desc = openInv[cgid]?.desc || closeInv[cgid]?.desc || recv[cgid]?.desc || deliv[cgid]?.desc || cgid;
      return { cgid, desc, subId: subIdFor(desc), opening: openInv[cgid] || { vol: 0, mass: 0 }, closing: closeInv[cgid] || { vol: 0, mass: 0 }, receipts: recv[cgid] || { vol: 0, mass: 0 }, deliveries: deliv[cgid] || { vol: 0, mass: 0 } };
    });
    return { grades };
  }, [inv, mov, datesInRange, effFrom, effTo, subIdFor]);

  const dailySeries = useMemo(() => {
    if (!datesInRange.length) return [];
    const invByDay: Record<string, number> = {};
    for (const r of inv) invByDay[r.dateKey] = (invByDay[r.dateKey] || 0) + (basis === 'volume' ? r.vol : r.mass);
    const movByDay: Record<string, { r: number; d: number }> = {};
    for (const m of mov) {
      if (m.dateKey && (m.dateKey < effFrom || m.dateKey > effTo)) continue;
      const b = (movByDay[m.dateKey] ||= { r: 0, d: 0 });
      const q = basis === 'volume' ? m.vol : m.mass;
      if (m.ind === 'R') b.r += q; if (m.ind === 'D') b.d += q;
    }
    return datesInRange.slice(1).map((day, i) => {
      const prev = datesInRange[i]; const md = movByDay[day] || { r: 0, d: 0 };
      const dInv = (invByDay[day] || 0) - (invByDay[prev] || 0); const netExt = md.r - md.d;
      return { date: day.slice(5), dInv: Math.round(dInv), netExt: Math.round(netExt), impliedMake: Math.round(dInv - netExt), receipts: Math.round(md.r), deliveries: Math.round(md.d) };
    });
  }, [inv, mov, datesInRange, effFrom, effTo, basis]);

  const oasGrouped = useMemo(() => {
    if (!oas) return null;
    const rows = oas.grades.filter((g) => group === 'ALL' || g.subId === group);
    const bySub = new Map<string, typeof rows>();
    rows.forEach((g) => { const a = bySub.get(g.subId) || []; a.push(g); bySub.set(g.subId, a); });
    const subs = Array.from(bySub.keys()).sort((a, b) => (CATEGORIES.findIndex((c) => c.id === subCategory(a)) - CATEGORIES.findIndex((c) => c.id === subCategory(b))) || subLabel(a).localeCompare(subLabel(b)));
    const dInv = (g: any) => val(g.closing) - val(g.opening);
    const netExt = (g: any) => val(g.receipts) - val(g.deliveries);
    const make = (g: any) => dInv(g) - netExt(g);
    const totals = { opening: 0, closing: 0, receipts: 0, deliveries: 0, dInv: 0, make: 0 };
    rows.forEach((g) => { totals.opening += val(g.opening); totals.closing += val(g.closing); totals.receipts += val(g.receipts); totals.deliveries += val(g.deliveries); totals.dInv += dInv(g); totals.make += make(g); });
    return { subs, bySub, dInv, netExt, make, totals };
  }, [oas, group, basis]); // eslint-disable-line react-hooks/exhaustive-deps

  // P2e — per-stream deltas between the currently-loaded month (full span) and the compare month.
  // Throughput = receipts + deliveries; Unexplained = ΔInventory − net external (the diagnostic).
  const compareRows = useMemo(() => {
    if (!cmpData) return null;
    const summarize = (invRows: InvRow[], movRows: MovRow[]) => {
      const m = new Map<string, { label: string; subId: string; thru: number; unexp: number }>();
      for (const g of buildGradeSummary(invRows, movRows, subIdFor)) {
        const key = g.desc || g.cgid;
        const thru = val(g.receipts) + val(g.deliveries);
        const unexp = (val(g.closing) - val(g.opening)) - (val(g.receipts) - val(g.deliveries));
        const cur = m.get(key) || { label: key, subId: g.subId, thru: 0, unexp: 0 };
        cur.thru += thru; cur.unexp += unexp; m.set(key, cur);
      }
      return m;
    };
    const cur = summarize(inv, mov);
    const prev = summarize(cmpData.inv, cmpData.mov);
    const keys = new Set<string>([...Array.from(cur.keys()), ...Array.from(prev.keys())]);
    const rows = Array.from(keys)
      .filter((k) => group === 'ALL' || (cur.get(k)?.subId || prev.get(k)?.subId) === group)
      .map((k) => {
        const c = cur.get(k); const p = prev.get(k);
        return {
          key: k, label: (c || p)!.label,
          curThru: c?.thru || 0, prevThru: p?.thru || 0,
          curUnexp: c?.unexp || 0, prevUnexp: p?.unexp || 0,
        };
      })
      .sort((a, b) => b.curThru - a.curThru);
    const totals = rows.reduce((t, r) => ({ curThru: t.curThru + r.curThru, prevThru: t.prevThru + r.prevThru, curUnexp: t.curUnexp + r.curUnexp, prevUnexp: t.prevUnexp + r.prevUnexp }), { curThru: 0, prevThru: 0, curUnexp: 0, prevUnexp: 0 });
    return { rows, totals };
  }, [cmpData, inv, mov, group, basis, subIdFor]); // eslint-disable-line react-hooks/exhaustive-deps

  // P2f — prioritize the streams Cal (and the operator) should worry about first: low accuracy
  // grade weighted by throughput. priority = size × risk(grade); unmapped streams read as risky
  // because their accuracy is unknown. This is the exact ranking the Cal action will surface.
  const riskWeight = (g?: string): number => (g === 'A' ? 1 : g === 'B' ? 2 : g === 'C' ? 4 : g === 'D' ? 8 : 6);
  const streamsToWatch = useMemo(() => {
    if (!streamRows.length) return [];
    const maxSize = Math.max(...streamRows.map((r) => r.size), 1);
    return streamRows
      .map((r) => { const grade = streamGrade(r.key); const w = riskWeight(grade); return { ...r, grade, weight: w, priority: (r.size / maxSize) * w }; })
      .filter((r) => r.size > 0)
      .sort((a, b) => b.priority - a.priority)
      .slice(0, 8);
  }, [streamRows, streamMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Daily Yields production balance (used when present — the closing balance) ----
  // Closing = Opening + Receipts − Deliveries + Intake&Prod + Adjustments (Own Consumption is
  // already embedded in Intake&Prod — subtracting it again double-counts, verified on the data).
  const resid = (r: YieldRow) => r.opening + r.receipts + r.intakeProd + r.adjustments - r.deliveries - r.closing;
  const yieldGrouped = useMemo(() => {
    if (!yields) return null;
    const rows = yields.rows.filter((r) => group === 'ALL' || r.group === group);
    const byGroup = new Map<string, YieldRow[]>();
    rows.forEach((r) => { const a = byGroup.get(r.group) || []; a.push(r); byGroup.set(r.group, a); });
    const groups = Array.from(byGroup.keys()).sort();
    const t = { opening: 0, receipts: 0, intakeProd: 0, deliveries: 0, ownCons: 0, adjustments: 0, closing: 0, netProd: 0, resid: 0 };
    rows.forEach((r) => { t.opening += r.opening; t.receipts += r.receipts; t.intakeProd += r.intakeProd; t.deliveries += r.deliveries; t.ownCons += r.ownCons; t.adjustments += r.adjustments; t.closing += r.closing; t.netProd += r.netProd; t.resid += resid(r); });
    return { groups, byGroup, totals: t };
  }, [yields, group]);

  const groupOptions = useMemo(() => {
    if (yields) return Array.from(new Set(yields.rows.map((r) => r.group))).sort();
    if (oas) return Array.from(new Set(oas.grades.map((g) => g.subId))).sort((a, b) => subLabel(a).localeCompare(subLabel(b)));
    return [];
  }, [yields, oas]);
  const groupOptionLabel = (id: string) => (yields ? id : subLabel(id));

  const hasData = inv.length > 0 || mov.length > 0 || !!yields;
  const hasOasFlow = inv.length > 0 || mov.length > 0;

  // ---------- render helpers ----------
  const kpi = (label: string, value: string, sub: string, color: string, tip?: string) => {
    const card = (
      <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 150 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}{tip && <InfoOutlined sx={{ fontSize: 13, ml: 0.5, verticalAlign: 'middle', opacity: 0.6 }} />}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color, mt: 0.5 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{sub}</Typography>
      </Paper>
    );
    return tip ? <Tooltip title={tip} key={label}>{card}</Tooltip> : <React.Fragment key={label}>{card}</React.Fragment>;
  };

  const mtd = yields?.mtd;

  return (
    <Box>
      {dsLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 1.25, borderRadius: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">Loading <strong>{dataset}</strong>…</Typography>
        </Box>
      )}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Diagnostic</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload <strong>Material Download</strong> (tank inventory + density/temperature), <strong>Ext Movement</strong>
        {' '}(receipts &amp; deliveries), and <strong>Daily Yields</strong> (per-grade balance + MTD loss). We join on
        {' '}<strong>COMMERCE_GRADE_ID</strong> and reconcile the balance, with an instant volume↔mass flip. Uploads are saved automatically and grouped by month.
      </Typography>

      {authExpired && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>Refresh</Button>}>
          Your session expired, so saved uploads couldn&apos;t load. <strong>Your data is safe</strong> — refresh to sign back in.
        </Alert>
      )}

      {/* Loaded-data status (upload is the single "Upload data" control at the top of the hub now) */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: (hasData && !dsLoading) ? 'action.hover' : 'transparent' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {dsLoading ? <CircularProgress size={20} /> : hasData ? <CheckCircle color="success" /> : <RadioButtonUnchecked color="disabled" />}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">
              {dsLoading ? <>Loading <strong>{dataset}</strong>…</> : fromRefinery
                ? <>Balance adapted from this dataset's Refinery data — {yields?.rows.length || 0} grades</>
                : <>Source files{inv.length ? ` · ${inv.length.toLocaleString()} inventory` : ''}{mov.length ? ` · ${mov.length} movements` : ''}{yields ? ` · ${yields.rows.length} yield grades` : ''}</>}
            </Typography>
            {!dsLoading && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {fromRefinery ? 'Density & Temperature diagnostics need tank-level density/temperature data (not present for this dataset).' : (fileNames.length ? fileNames.join('  ·  ') : 'No source data for this dataset yet — use “Upload data” at the top of the page.')}
              </Typography>
            )}
          </Box>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">Saved to the <strong>{dataset || 'active'}</strong> dataset · view or delete files from <strong>Manage files</strong> at the top of the page.</Typography>
      </Stack>

      {/* Month selector — one chip per saved month (newest first); click to view that complete month. */}
      {periods.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Month:</Typography>
          {[...periods].sort().map((p) => (
            <Chip key={p} label={monthLabel(p)} size="small" clickable
              color={activePeriod === p ? 'primary' : 'default'}
              variant={activePeriod === p ? 'filled' : 'outlined'}
              onClick={() => { void hydrateFromPeriod(p); }} />
          ))}
        </Stack>
      )}

      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {hasData && (
        <>
          {/* View + basis */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <ToggleButtonGroup size="small" exclusive value={view} onChange={(_e, v) => v && setView(v)}>
              <ToggleButton value="balance">Balance</ToggleButton>
              <ToggleButton value="density">Density &amp; Temp</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ flex: 1 }} />
            {activePeriod && (
              <Chip size="small" clickable color="secondary" variant="outlined" icon={<QuestionAnswer sx={{ fontSize: 16 }} />}
                label="Monthly Mass Balance Checklist" onClick={() => setChecklistOpen(true)} />
            )}
            {view === 'balance' && (
              <ToggleButtonGroup size="small" exclusive value={basis} onChange={(_e, v) => v && setBasis(v)}>
                <ToggleButton value="volume">Volume (m³)</ToggleButton>
                <ToggleButton value="mass">Mass (t)</ToggleButton>
              </ToggleButtonGroup>
            )}
            {view === 'balance' && streamRows.length > 0 && (
              <Button variant="outlined" size="small" onClick={() => setStreamOpen(true)}>Stream mapping</Button>
            )}
            <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={reset}>Reset</Button>
          </Stack>

          <MonthEndChecklist open={checklistOpen} onClose={() => setChecklistOpen(false)}
            dataset={dataset || ''} period={activePeriod || ''} utility="oas" />

          <StreamMapping open={streamOpen} onClose={() => setStreamOpen(false)} streams={streamRows}
            mapping={streamMap} onChange={setStreamMap} onSave={saveStream} saving={savingStream} unit={unit} />

          {view === 'density' ? (
            (fromRefinery || inv.length === 0) ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No <strong>density / temperature</strong> data in this dataset. The density diagnostics come from the
                <strong> Material Download</strong> (per-tank, per-day density &amp; temperature){fromRefinery ? ', which a refinery-sourced dataset like this one does not contain' : ''}.
                The <strong>Balance</strong> view is still available.
              </Alert>
            ) : (
              <MassBalanceDensity inv={inv} mov={mov} />
            )
          ) : (
            <>
              {/* scope banner */}
              {yields ? (
                <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                  Closing balance from <strong>Daily Yields</strong> (the customer&apos;s per-grade balance + MTD loss), enriched by
                  the inventory &amp; movement detail. Crude-in (KDR) and the reconciled leg are still pending.
                </Alert>
              ) : (
                <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
                  <strong>Inventory + external-movement</strong> reconciliation from the source files. Add <strong>Daily Yields</strong>
                  {' '}for the production/intake leg (real loss/gain). Headline below is <strong>implied net production</strong>, not a loss.
                </Alert>
              )}

              {/* Controls */}
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TextField select size="small" label="Group" value={group} onChange={(e) => setGroup(e.target.value)} sx={{ minWidth: 180 }}>
                    <MenuItem value="ALL"><em>All groups</em></MenuItem>
                    {groupOptions.map((id) => <MenuItem key={id} value={id}>{groupOptionLabel(id)}</MenuItem>)}
                  </TextField>
                  {allDates.length > 0 && (
                    <>
                      <TextField select size="small" label="From" value={effFrom} onChange={(e) => setFromDate(e.target.value)} sx={{ minWidth: 130 }}>
                        {allDates.map((d) => <MenuItem key={d} value={d} disabled={d > effTo}>{d}</MenuItem>)}
                      </TextField>
                      <TextField select size="small" label="To" value={effTo} onChange={(e) => setToDate(e.target.value)} sx={{ minWidth: 130 }}>
                        {allDates.map((d) => <MenuItem key={d} value={d} disabled={d < effFrom}>{d}</MenuItem>)}
                      </TextField>
                    </>
                  )}
                  {periods.length > 0 && (
                    <>
                      <Box sx={{ flex: 1 }} />
                      <TextField select size="small" label="Compare vs" value={cmpPeriod} onChange={(e) => loadCompare(e.target.value)} sx={{ minWidth: 150 }}
                        helperText={cmpLoading ? 'Loading…' : undefined}>
                        <MenuItem value=""><em>None</em></MenuItem>
                        {[...periods].sort().map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                      </TextField>
                    </>
                  )}
                </Stack>
              </Paper>

              {/* KPI cards */}
              {yields && mtd ? (
                <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  {kpi('Crude Processed', mtd.crudeProcessed !== null ? `${fmt(mtd.crudeProcessed)}` : '—', 'MT · month', 'primary.main')}
                  {kpi('Total Fuel', mtd.totalFuel !== null ? `${fmt(mtd.totalFuel)}` : '—', `MT${mtd.fuelPct !== null ? ` · ${(mtd.fuelPct * 100).toFixed(2)}%` : ''}`, 'text.primary')}
                  {kpi('Unaccounted Loss', mtd.unaccountedLoss !== null ? `${signed(-Math.abs(mtd.unaccountedLoss))}` : '—', 'MT · measured', 'error.main')}
                  {kpi('Loss %', mtd.lossPct !== null ? `${(mtd.lossPct * 100).toFixed(2)}%` : '—', 'of crude', mtd.lossPct !== null && Math.abs(mtd.lossPct) > 0.005 ? 'error.main' : 'success.main')}
                  {kpi('Reconciled', '—', 'awaiting mass-balance file', 'text.disabled')}
                </Stack>
              ) : oasGrouped ? (
                <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  {kpi('Receipts (in)', fmt(oasGrouped.totals.receipts), `${unit} · external`, 'success.main')}
                  {kpi('Deliveries (out)', fmt(oasGrouped.totals.deliveries), `${unit} · external`, 'primary.main')}
                  {kpi('Δ Inventory', signed(oasGrouped.totals.dInv), `${unit} · close − open`, oasGrouped.totals.dInv < 0 ? 'warning.main' : 'text.primary')}
                  {kpi('Implied Net Production', signed(oasGrouped.totals.make), `${unit} · ΔInv − net move`, 'text.primary', 'ΔInventory − (Receipts − Deliveries). Crude-in is not in the Ext Movement file, so crude reads as an inventory swing until KDR is added.')}
                  {kpi('Reconciled', '—', 'awaiting mass-balance file', 'text.disabled')}
                </Stack>
              ) : null}

              {/* P2f — priority: low-accuracy, high-volume streams to watch */}
              {streamsToWatch.length > 0 && (
                <Paper variant="outlined" sx={{ mb: 2, p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2">Streams to watch</Typography>
                    <Chip size="small" variant="outlined" label="accuracy × volume" />
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Ranked by risk = throughput weighted by accuracy grade{streamRows.some((r) => !streamGrade(r.key)) ? ' · untagged streams count as unknown accuracy' : ''}.
                      {' '}<Button size="small" onClick={() => setStreamOpen(true)} sx={{ minWidth: 0, p: 0, verticalAlign: 'baseline' }}>Tag accuracy</Button>
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {streamsToWatch.map((s, i) => (
                      <Paper key={s.key} variant="outlined" sx={{ px: 1.25, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, borderColor: i === 0 ? 'error.main' : 'divider' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{i + 1}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.label}</Typography>
                        <Box component="span" sx={{ px: 0.55, py: '1px', borderRadius: '3px', fontSize: '0.62rem', fontWeight: 700, color: '#fff', bgcolor: gradeColor(s.grade) }}>{s.grade || '?'}</Box>
                        <Typography variant="caption" color="text.secondary">{fmt(s.size)} {unit}</Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Paper>
              )}

              {/* P2e — month-over-month comparison */}
              {compareRows && (
                <Paper variant="outlined" sx={{ mb: 2 }}>
                  <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2">Compare — this month vs {cmpPeriod}</Typography>
                    <Chip size="small" variant="outlined" label={`${unit} · ${compareRows.rows.length} streams`} />
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">Δ = current − {cmpPeriod}. Streams sorted by current throughput.</Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Stream</TableCell>
                          <TableCell align="right">Throughput</TableCell>
                          <TableCell align="right">{cmpPeriod}</TableCell>
                          <TableCell align="right">Δ Throughput</TableCell>
                          <TableCell align="right">Unexplained</TableCell>
                          <TableCell align="right">{cmpPeriod}</TableCell>
                          <TableCell align="right">Δ Unexplained</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {compareRows.rows.map((r) => {
                          const dThru = r.curThru - r.prevThru; const dUnexp = r.curUnexp - r.prevUnexp;
                          return (
                            <TableRow key={r.key} hover>
                              <TableCell>{r.label}{gradeBadge(r.key)}</TableCell>
                              <TableCell align="right">{fmt(r.curThru)}</TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmt(r.prevThru)}</TableCell>
                              <TableCell align="right" sx={{ color: dThru >= 0 ? 'success.main' : 'warning.main' }}>{signed(dThru)}</TableCell>
                              <TableCell align="right">{signed(r.curUnexp)}</TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>{signed(r.prevUnexp)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: Math.abs(dUnexp) > Math.abs(r.prevUnexp) ? 'error.main' : 'text.secondary' }}>{signed(dUnexp)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(compareRows.totals.curThru)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>{fmt(compareRows.totals.prevThru)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: (compareRows.totals.curThru - compareRows.totals.prevThru) >= 0 ? 'success.main' : 'warning.main' }}>{signed(compareRows.totals.curThru - compareRows.totals.prevThru)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{signed(compareRows.totals.curUnexp)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>{signed(compareRows.totals.prevUnexp)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{signed(compareRows.totals.curUnexp - compareRows.totals.prevUnexp)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* Metric toggle */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2">Balance view:</Typography>
                <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_e, v) => v && setMetric(v)}>
                  {METRIC_TABS.map((m) => <ToggleButton key={m} value={m}>{m}</ToggleButton>)}
                </ToggleButtonGroup>
                {metric !== 'Measured' && (
                  <Chip size="small" icon={<InfoOutlined />} label="Stub — needs the mass-balance export" variant="outlined" />
                )}
              </Stack>

              {metric !== 'Measured' ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>{metric}</strong> view needs the mass-balance export, which has not been
                  ingested yet. The <strong>Measured</strong> view is fully live from the uploaded data.
                </Alert>
              ) : (
                <>
                  {/* OAS trend charts (shown whenever OAS inventory/movement present) */}
                  {hasOasFlow && dailySeries.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Daily inventory change vs net external movement ({unit}/day)</Typography>
                      <ResponsiveContainer width="100%" height={240}>
                        <ComposedChart data={dailySeries} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`)} />
                          <RTooltip contentStyle={{ fontSize: 12 }} formatter={(v: any) => fmt(Number(v))} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <ReferenceLine y={0} stroke="#888" />
                          <Line dataKey="dInv" name="Δ Inventory" stroke="#1f4e79" strokeWidth={2} dot={{ r: 2 }} />
                          <Line dataKey="netExt" name="Net movement (R−D)" stroke="#2e7d32" strokeWidth={2} dot={{ r: 2 }} />
                          <Bar dataKey="deliveries" name="Deliveries" fill="#1976d2" opacity={0.25} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  )}

                  {/* Balance table: Daily Yields production balance (preferred) or OAS volume balance */}
                  {yieldGrouped ? (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Production Balance — Daily Yields &quot;Movements m³&quot; (m³)</Typography>
                      <Table size="small" sx={{ minWidth: 960 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Group / Grade</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Opening</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>+ Receipts</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>+ Intake&amp;Prod</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>− Deliveries</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>± Adj</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Closing</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Δ close</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {yieldGrouped.groups.map((gid) => {
                            const rows = yieldGrouped.byGroup.get(gid) || [];
                            const st = { opening: 0, receipts: 0, intakeProd: 0, deliveries: 0, ownCons: 0, adjustments: 0, closing: 0, resid: 0 };
                            rows.forEach((r) => { st.opening += r.opening; st.receipts += r.receipts; st.intakeProd += r.intakeProd; st.deliveries += r.deliveries; st.ownCons += r.ownCons; st.adjustments += r.adjustments; st.closing += r.closing; st.resid += resid(r); });
                            const open = expanded[gid];
                            return (
                              <React.Fragment key={gid}>
                                <TableRow hover sx={{ bgcolor: 'action.hover', cursor: 'pointer' }} onClick={() => setExpanded((e) => ({ ...e, [gid]: !e[gid] }))}>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>{open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}</IconButton>
                                    {gid} <Typography component="span" variant="caption" color="text.secondary">({rows.length})</Typography>
                                  </TableCell>
                                  <TableCell align="right">{fmt(st.opening)}</TableCell>
                                  <TableCell align="right" sx={{ color: 'success.main' }}>{fmt(st.receipts)}</TableCell>
                                  <TableCell align="right" sx={{ color: 'success.main' }}>{signed(st.intakeProd)}</TableCell>
                                  <TableCell align="right" sx={{ color: 'primary.main' }}>{fmt(st.deliveries)}</TableCell>
                                  <TableCell align="right">{signed(st.adjustments)}</TableCell>
                                  <TableCell align="right">{fmt(st.closing)}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, color: Math.abs(st.resid) > 1 ? 'warning.main' : 'text.secondary' }}>{signed(st.resid)}</TableCell>
                                </TableRow>
                                {open && rows.slice().sort((a, b) => bySizeThenGrade(a.desc || a.sap, b.desc || b.sap)).map((r) => (
                                  <TableRow key={r.sap} hover>
                                    <TableCell sx={{ pl: 5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}><Typography variant="body2">{r.desc || r.sap}</Typography>{gradeBadge(r.desc || r.sap)}</Box>
                                      <Typography variant="caption" color="text.secondary">{r.sap}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{fmt(r.opening)}</TableCell>
                                    <TableCell align="right">{fmt(r.receipts)}</TableCell>
                                    <TableCell align="right">{signed(r.intakeProd)}</TableCell>
                                    <TableCell align="right">{fmt(r.deliveries)}</TableCell>
                                    <TableCell align="right">{signed(r.adjustments)}</TableCell>
                                    <TableCell align="right">{fmt(r.closing)}</TableCell>
                                    <TableCell align="right" sx={{ color: Math.abs(resid(r)) > 1 ? 'warning.main' : 'text.secondary' }}>{signed(resid(r))}</TableCell>
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            );
                          })}
                          <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                            <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(yieldGrouped.totals.opening)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(yieldGrouped.totals.receipts)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{signed(yieldGrouped.totals.intakeProd)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(yieldGrouped.totals.deliveries)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{signed(yieldGrouped.totals.adjustments)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(yieldGrouped.totals.closing)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{signed(yieldGrouped.totals.resid)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Δ close = Opening + Receipts + Intake&amp;Prod + Adj − Deliveries − Closing (≈0 when the grade balances;
                        Own Consumption is already inside Intake&amp;Prod). Daily Yields is m³; the MTD loss above is the mass (MT) reconciliation.
                      </Typography>
                    </Paper>
                  ) : oasGrouped ? (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Volume Balance ({unit}) — {effFrom} → {effTo}</Typography>
                      <Table size="small" sx={{ minWidth: 820 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Category / Grade</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Opening</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>+ Receipts</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>− Deliveries</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Closing</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Δ Inv</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Implied make</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {oasGrouped.subs.map((sid) => {
                            const rows = oasGrouped.bySub.get(sid) || [];
                            const st = { opening: 0, receipts: 0, deliveries: 0, closing: 0, dInv: 0, make: 0 };
                            rows.forEach((g) => { st.opening += val(g.opening); st.receipts += val(g.receipts); st.deliveries += val(g.deliveries); st.closing += val(g.closing); st.dInv += oasGrouped.dInv(g); st.make += oasGrouped.make(g); });
                            const open = expanded[sid];
                            return (
                              <React.Fragment key={sid}>
                                <TableRow hover sx={{ bgcolor: 'action.hover', cursor: 'pointer' }} onClick={() => setExpanded((e) => ({ ...e, [sid]: !e[sid] }))}>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>{open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}</IconButton>
                                    {subLabel(sid)} <Typography component="span" variant="caption" color="text.secondary">({rows.length})</Typography>
                                  </TableCell>
                                  <TableCell align="right">{fmt(st.opening)}</TableCell>
                                  <TableCell align="right" sx={{ color: 'success.main' }}>{fmt(st.receipts)}</TableCell>
                                  <TableCell align="right" sx={{ color: 'primary.main' }}>{fmt(st.deliveries)}</TableCell>
                                  <TableCell align="right">{fmt(st.closing)}</TableCell>
                                  <TableCell align="right">{signed(st.dInv)}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, color: st.make < 0 ? 'warning.main' : 'success.main' }}>{signed(st.make)}</TableCell>
                                </TableRow>
                                {open && rows.slice().sort((a, b) => bySizeThenGrade(a.desc || a.cgid, b.desc || b.cgid)).map((g) => (
                                  <TableRow key={g.cgid} hover>
                                    <TableCell sx={{ pl: 5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}><Typography variant="body2">{g.desc || g.cgid}</Typography>{gradeBadge(g.desc || g.cgid)}</Box>
                                      <Typography variant="caption" color="text.secondary">{g.cgid}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{fmt(val(g.opening))}</TableCell>
                                    <TableCell align="right">{fmt(val(g.receipts))}</TableCell>
                                    <TableCell align="right">{fmt(val(g.deliveries))}</TableCell>
                                    <TableCell align="right">{fmt(val(g.closing))}</TableCell>
                                    <TableCell align="right">{signed(oasGrouped.dInv(g))}</TableCell>
                                    <TableCell align="right" sx={{ color: oasGrouped.make(g) < 0 ? 'warning.main' : 'success.main' }}>{signed(oasGrouped.make(g))}</TableCell>
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            );
                          })}
                          <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                            <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(oasGrouped.totals.opening)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(oasGrouped.totals.receipts)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(oasGrouped.totals.deliveries)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(oasGrouped.totals.closing)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{signed(oasGrouped.totals.dInv)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>{signed(oasGrouped.totals.make)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Implied make = Δ Inventory − (Receipts − Deliveries). Positive ≈ product made; negative ≈ feed consumed.
                        Crude-in isn&apos;t in the Ext Movement file, so crude reads as an inventory swing — add Daily Yields to close it.
                      </Typography>
                    </Paper>
                  ) : null}
                </>
              )}
            </>
          )}
        </>
      )}
      {/* Saved-uploads manager: flip months, load, delete */}
      <Dialog open={mgrOpen} onClose={() => setMgrOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Saved uploads</DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Month" value={mgrPeriod} onChange={(e) => selectMgrPeriod(e.target.value)} sx={{ minWidth: 160 }}>
              {[...periods].sort().map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
            <Button size="small" variant="contained" onClick={loadStoredMonth} disabled={!mgrPeriod || mgrLoading}>Load this month</Button>
            {mgrLoading && <CircularProgress size={18} />}
          </Stack>
          {mgrFiles.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{mgrLoading ? 'Loading…' : 'No files stored for this month.'}</Typography>
          ) : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ fontWeight: 700 }}>File</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Kind</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Size</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }} />
              </TableRow></TableHead>
              <TableBody>
                {mgrFiles.map((sf) => (
                  <TableRow key={sf.uploadId} hover>
                    <TableCell>{sf.url ? <a href={sf.url} target="_blank" rel="noreferrer">{sf.filename}</a> : sf.filename}</TableCell>
                    <TableCell>{sf.kind}</TableCell>
                    <TableCell align="right">{sf.size ? `${Math.round(sf.size / 1024)} KB` : '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete this file"><IconButton size="small" onClick={() => removeStored(sf)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setMgrOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!storeMsg} autoHideDuration={5000} onClose={() => setStoreMsg('')} message={storeMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
});

export default MassBalanceReconciliation;
